import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { Option } from "@/shared/components/molecules/OptionsSelect";
import {
  configureExporting,
  generateYearsOptions,
  initializeAmChart,
  Language,
  months,
} from "@/shared/utils/chartUtils";
import { Branch } from "@/app/branchs/branchs.type";
import * as am5 from "@amcharts/amcharts5";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5xy from "@amcharts/amcharts5/xy";
import { Select } from "@/shared/components/organisms/Select";
import ScrollableSelectContainer from "@/shared/components/organisms/ScrollableSelectContainer";
import { useGetBenefitsQuery } from "@/app/kpi/kpi.api";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type BenefitsChartProps = {
  branches: Branch[];
};

const BenefitsChart: React.FC<BenefitsChartProps> = ({ branches }) => {
  const [isLoading, setIsLoading] = useState(true);
  //const [branchSelected, setBranchSelected] = useState<Option>();
  const [branchesOptions, setBranchesOptions] = useState<Option[]>([]);
  //const [yearSelected, setYearSelected] = useState<Option>();
  //const [currencyEarningsTotal, setCurrencyEarningsTotal] = useState<Option>();
  const [currencyEarningsTotalOptions, setCurrencyEarningsTotalOptions] =
    useState<Option[]>([]);
  const yearsOptions = generateYearsOptions(
    new Date().getFullYear() - 10,
    new Date().getFullYear()
  );

  const [branchSelected, setBranchSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("branchSelected");
    return saved ? JSON.parse(saved) : undefined;
  });

  const [currencyEarningsTotal, setCurrencyEarningsTotal] = useState<Option>(
    () => {
      const saved = localStorage.getItem("currencyEarningsTotal");
      return saved ? JSON.parse(saved) : undefined;
    }
  );

  const [yearSelected, setYearSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("yearSelected");
    return saved ? JSON.parse(saved) : undefined;
  });

  const { data: dataChart } = useGetBenefitsQuery({
    filial: branchSelected?.id,
    currency_request: currencyEarningsTotal?.id,
    year: yearSelected?.id,
  });

  useEffect(() => {
    if (branches) {
      setBranchesOptions(
        branches.map((branch: Branch) => ({
          id: branch.id,
          name: branch.branch,
        }))
      );
    }
  }, [branches]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();

    if (branchesOptions.length > 0 && !branchSelected) {
      setBranchSelected(branchesOptions[0]);
    }
    if (currencyEarningsTotalOptions.length > 0 && !currencyEarningsTotal) {
      setCurrencyEarningsTotal(currencyEarningsTotalOptions[0]);
    }
    if (yearsOptions.length > 0 && !yearSelected) {
      const defaultYear = yearsOptions.find((year) => year.id === currentYear);
      setYearSelected(defaultYear || yearsOptions[0]);
    }
  }, [
    branchesOptions,
    yearsOptions,
    currencyEarningsTotalOptions,
    branchSelected,
    yearSelected,
    currencyEarningsTotal,
  ]);

  useEffect(() => {
    const systemLanguage: Language = navigator.language.slice(0, 2) as Language;
    const selectedMonths = months[systemLanguage] || months["en"];

    if (dataChart) {
      const transformDataChart = dataChart.data.map(
        (item: any, index: number) => {
          return {
            category: selectedMonths[index],
            value: item,
          };
        }
      );

      const maxValue = Math.max(
        ...dataChart.data.map((item: any) => item.value)
      );
      const minValue = Math.min(
        ...dataChart.data.map((item: any) => item.value)
      );

      const currencies = Object.keys(dataChart.currency).map((key) => ({
        id: key,
        name: dataChart.currency[key],
      }));

      setCurrencyEarningsTotalOptions(currencies);

      const currency = dataChart.currency_request.toUpperCase();

      createBenefits(transformDataChart, currency, minValue, maxValue);
    }
  }, [dataChart]);

  function createBenefits(
    data: any,
    currency: any,
    minValue: any,
    maxValue: any
  ) {
    am5.ready(function () {
      const root = initializeAmChart("chart_benefits");

      root.setThemes([am5themes_Animated.new(root)]);

      if (root._logo) {
        root._logo.dispose();
      }

      if (!data || data.length === 0) {
        root.container.children.push(
          am5.Label.new(root, {
            text: "Información no disponible",
            fontSize: 20,
            fill: am5.color("#cbcac8"),
            centerX: am5.p50,
            centerY: am5.p50,
            x: am5.p50,
            y: am5.p50,
            horizontalCenter: "middle",
            verticalCenter: "middle",
          })
        );

        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return;
      }

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: false,
          wheelX: undefined,
          wheelY: undefined,
          pinchZoomX: true,
          paddingLeft: 0,
          paddingRight: 1,
        })
      );

      const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineY.set("visible", false);

      const xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        minGridDistance: 30,
        minorGridEnabled: true,
      });

      xRenderer.labels.template.setAll({
        centerY: am5.p50,
        centerX: am5.p100,
        paddingRight: 15,
      });

      xRenderer.grid.template.setAll({
        location: 1,
        visible: false,
      });

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          maxDeviation: 0.3,
          categoryField: "category",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      );

      xAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
        fill: am5.color(0x6b7280), // text-gray-500
      });

      const yRenderer = am5xy.AxisRendererY.new(root, {
        strokeOpacity: 0.1,
      });

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
          min: minValue < 0 ? minValue : 0,
          max: maxValue < 5 ? 5 : maxValue,
          strictMinMax: true,
          numberFormat: "#",
        })
      );

      xAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
      });

      yAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
      });

      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: "Series 1",
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          sequencedInterpolation: true,
          categoryXField: "category",
          tooltip: am5.Tooltip.new(root, {
            labelText: `${t(
              "share.views.benefits"
            )} {valueY} ${currency.toUpperCase()}`,
          }),
        })
      );

      series.columns.template.setAll({
        tooltipText: "{name}, {categoryX}: {valueY}" + currency,
        cornerRadiusTL: 2,
        cornerRadiusTR: 2,
        strokeOpacity: 0,
        fill: am5.color("#68478D"),
      });

      xAxis.data.setAll(data);
      series.data.setAll(data);

      configureExporting(root, data);

      setIsLoading(false);

      series.appear(1000);
      chart.appear(1000, 100);
    }); // end
  }

  return (
    <>
      <h2 className="text-center font-bold leading-6 text-gray-900">
        {t("user.views.main_title_benefits_total_month")}
      </h2>
      <ScrollableSelectContainer>
        <Select
          name="branch"
          selected={branchSelected}
          setSelected={setBranchSelected}
          options={branchesOptions}
          tittle={t("user.views.branch")}
        />
        <Select
          name="currency"
          selected={currencyEarningsTotal}
          setSelected={setCurrencyEarningsTotal}
          options={currencyEarningsTotalOptions}
          tittle={t("user.views.currency")}
        />
        <Select
          name="year"
          selected={yearSelected}
          setSelected={setYearSelected}
          options={yearsOptions}
          tittle={t("user.views.year")}
        />
      </ScrollableSelectContainer>

      <div
        style={{ position: "relative" }}
        className="xl:mt-2 md:mt-16 sm:mt-10"
      >
        {isLoading && (
          <div className="flex justify-center items-center h-90 mt-20">
            <FontAwesomeIcon icon={faLoader} spin className="text-6xl" />
          </div>
        )}
        <div
          id="chart_benefits"
          style={{ width: "100%", height: "400px" }}
        ></div>
      </div>
    </>
  );
};

export default BenefitsChart;
